/**
 * Firm Task Templates API Service
 *
 * This service provides hooks for managing firm task templates.
 * The types and hooks here are placeholders that will be replaced
 * by generated code once the backend API is available.
 *
 * Run `pnpm generate:api` after backend deploys to get the real types.
 */
import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from './baseQuery';

// =============================================================================
// Types (placeholders - will be replaced by generated types)
// =============================================================================

export type FirmTaskTemplateStatus = 'auto_create' | 'manual_only' | 'archived';

export interface FirmTaskTemplateSchema {
  id: string;
  firm_id: string;
  name: string;
  description: string;
  status: FirmTaskTemplateStatus;
  task_type: string;
  task_group: string;
  assignee_role: string | null;
  sla_resolution_policy: string | null;
  /** UUID of the user who created the template */
  created_by_id: string | null;
  /** Name of the user who created the template (null for system-created or legacy templates) */
  created_by_name: string | null;
  created_at: string;
  modified_at: string;
}

export interface CreateFirmTaskTemplateRequest {
  name: string;
  description?: string;
  task_type: string;
  assignee_role?: string | null;
  sla_resolution_policy?: string | null;
  status?: FirmTaskTemplateStatus;
}

export interface UpdateFirmTaskTemplateRequest {
  name?: string;
  description?: string;
  task_type?: string;
  assignee_role?: string | null;
  sla_resolution_policy?: string | null;
  status?: FirmTaskTemplateStatus;
}

export interface TaskTemplateOptionsResponse {
  assignee_roles: { value: string; label: string }[];
  sla_policies: { value: string; label: string }[];
  task_types: { value: string; label: string }[];
  statuses: { value: FirmTaskTemplateStatus; label: string }[];
}

export interface ApplyPreviewRequest {
  matter_ids: string[];
}

export interface ApplyPreviewResponse {
  total_matters: number;
  will_create: number;
  already_exists: number;
}

export interface ApplyRequest {
  matter_ids: string[];
}

export interface ApplyResponse {
  workflow_id: string;
  status: string;
  total_matters: number;
}

// =============================================================================
// API Service
// =============================================================================

export const firmTaskTemplatesApi = createApi({
  reducerPath: 'firmTaskTemplatesApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['FirmTaskTemplates', 'FirmTaskTemplateOptions'],
  endpoints: (builder) => ({
    // List all task templates for a firm
    getFirmTaskTemplates: builder.query<FirmTaskTemplateSchema[], string>({
      query: (firmId) => `api/firms/${firmId}/task-templates/`,
      providesTags: (result, error, firmId) => [
        { type: 'FirmTaskTemplates', id: firmId },
        ...(result?.map((t) => ({
          type: 'FirmTaskTemplates' as const,
          id: t.id,
        })) ?? []),
      ],
    }),

    // Get dropdown options for task templates
    getFirmTaskTemplateOptions: builder.query<
      TaskTemplateOptionsResponse,
      string
    >({
      query: (firmId) => `api/firms/${firmId}/task-templates/options/`,
      providesTags: (result, error, firmId) => [
        { type: 'FirmTaskTemplateOptions', id: firmId },
      ],
    }),

    // Get a single task template
    getFirmTaskTemplate: builder.query<
      FirmTaskTemplateSchema,
      { firmId: string; templateId: string }
    >({
      query: ({ firmId, templateId }) =>
        `api/firms/${firmId}/task-templates/${templateId}/`,
      providesTags: (result, error, { templateId }) => [
        { type: 'FirmTaskTemplates', id: templateId },
      ],
    }),

    // Create a new task template
    createFirmTaskTemplate: builder.mutation<
      FirmTaskTemplateSchema,
      { firmId: string; data: CreateFirmTaskTemplateRequest }
    >({
      query: ({ firmId, data }) => ({
        url: `api/firms/${firmId}/task-templates/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { firmId }) => [
        { type: 'FirmTaskTemplates', id: firmId },
      ],
    }),

    // Update a task template
    updateFirmTaskTemplate: builder.mutation<
      FirmTaskTemplateSchema,
      {
        firmId: string;
        templateId: string;
        data: UpdateFirmTaskTemplateRequest;
      }
    >({
      query: ({ firmId, templateId, data }) => ({
        url: `api/firms/${firmId}/task-templates/${templateId}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { firmId, templateId }) => [
        { type: 'FirmTaskTemplates', id: firmId },
        { type: 'FirmTaskTemplates', id: templateId },
      ],
    }),

    // Preview applying a task template to matters
    previewApplyFirmTaskTemplate: builder.mutation<
      ApplyPreviewResponse,
      { firmId: string; templateId: string; data: ApplyPreviewRequest }
    >({
      query: ({ firmId, templateId, data }) => ({
        url: `api/firms/${firmId}/task-templates/${templateId}/apply/preview/`,
        method: 'POST',
        body: data,
      }),
    }),

    // Apply a task template to matters
    applyFirmTaskTemplate: builder.mutation<
      ApplyResponse,
      { firmId: string; templateId: string; data: ApplyRequest }
    >({
      query: ({ firmId, templateId, data }) => ({
        url: `api/firms/${firmId}/task-templates/${templateId}/apply/`,
        method: 'POST',
        body: data,
      }),
    }),
  }),
});

export const {
  useGetFirmTaskTemplatesQuery,
  useGetFirmTaskTemplateOptionsQuery,
  useGetFirmTaskTemplateQuery,
  useCreateFirmTaskTemplateMutation,
  useUpdateFirmTaskTemplateMutation,
  usePreviewApplyFirmTaskTemplateMutation,
  useApplyFirmTaskTemplateMutation,
} = firmTaskTemplatesApi;
